import React, { useState, useEffect } from 'react';
import { 
  X, Bell, Package, Heart, Info, CheckCircle, Trash2, 
  Tag, ArrowRight, UserPlus, Trophy, MessageCircle, Clock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { db } from '../../services/firebase';
import { doc, writeBatch, updateDoc, arrayUnion, deleteDoc } from 'firebase/firestore';

interface Notification {
  id: string;
  // 🟢 ADDED 'drop_reminder' to the types
  type: 'order' | 'follow' | 'follow_request' | 'like' | 'sale' | 'general' | 'reward' | 'mention' | 'message' | 'drop_reminder';
  title: string;
  message: string;
  read: boolean;
  createdAt: any;
  data?: any; 
  status?: string;
  senderId?: string;
}

interface NotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: any[]; 
  userId: string;
}

export const NotificationsModal: React.FC<NotificationsModalProps> = ({ 
  isOpen, 
  onClose, 
  notifications,
  userId 
}) => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleMarkAllRead = async () => {
    if (!userId || notifications.length === 0) return;
    setIsProcessing(true);

    try {
      const batch = writeBatch(db);
      const unread = notifications.filter((n: any) => !n.read);

      if (unread.length === 0) {
        setIsProcessing(false);
        return;
      }

      unread.forEach((n: any) => {
        const ref = doc(db, "notifications", n.id);
        batch.update(ref, { read: true });
      });

      await batch.commit();
    } catch (error) {
      console.error("Error marking all read:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClearAll = async () => {
    if (!userId || notifications.length === 0) return;
    if (!window.confirm("Delete all notifications?")) return;
    
    setIsProcessing(true);
    try {
      const batch = writeBatch(db);
      notifications.forEach((n: any) => {
        const ref = doc(db, "notifications", n.id);
        batch.delete(ref);
      });
      await batch.commit();
    } catch (error) {
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAcceptFollow = async (e: React.MouseEvent, notificationId: string, requesterId: string) => {
      e.stopPropagation(); 
      if (!userId || !requesterId) return;

      try {
          await updateDoc(doc(db, 'users', userId), { followers: arrayUnion(requesterId) });
          await updateDoc(doc(db, 'users', requesterId), { followingUsers: arrayUnion(userId) });
          await updateDoc(doc(db, 'notifications', notificationId), {
              status: 'accepted',
              title: 'Request Accepted',
              message: 'You have accepted this follow request.',
              read: true
          });
      } catch (error) {
          console.error('Error accepting follow request:', error);
      }
  };

  const handleDeclineFollow = async (e: React.MouseEvent, notificationId: string) => {
      e.stopPropagation(); 
      try {
          await deleteDoc(doc(db, 'notifications', notificationId));
      } catch (error) {
          console.error('Error declining follow request:', error);
      }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      try {
        const ref = doc(db, "notifications", notification.id);
        await updateDoc(ref, { read: true });
      } catch (e) {
        console.error("Failed to mark notification as read", e);
      }
    }
    
    onClose();

    // 🟢 SMART ROUTING
    try {
      switch (notification.type) {
        case 'reward':
          navigate('/profile', { state: { activeTab: 'wallet', openVault: true } });
          break;
        case 'order':
          navigate('/profile', { state: { activeTab: 'orders' } });
          break;
        case 'mention':
        case 'message':
           window.dispatchEvent(new Event('open-messages'));
           break;
        case 'follow':
          if (notification.data?.followerId) navigate(`/profile/${notification.data.followerId}`);
          break;
        case 'drop_reminder':
          // 🟢 ADDED: Route to home or product page when clicked
          navigate('/');
          break;
        default:
          break;
      }
    } catch (error) {
       console.error("Navigation failed", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 lg:top-[80px] z-[200] lg:z-[40] pointer-events-none">
      
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm pointer-events-auto animate-in fade-in duration-500" 
        onClick={onClose} 
      />

      <div className="absolute top-0 left-0 right-0 w-full h-full lg:h-auto lg:max-h-[calc(100vh-80px)] bg-black lg:border-b border-zinc-800 shadow-2xl flex flex-col pointer-events-auto animate-in slide-in-from-right lg:slide-in-from-top-full duration-500 ease-out">
        
        <div className="px-6 lg:px-12 py-6 border-b border-zinc-800 flex items-center justify-between bg-black sticky top-0 z-10">
          <h2 className="text-3xl lg:text-4xl font-black text-white uppercase italic tracking-tighter">
            Alerts <span className="text-lg lg:text-xl not-italic font-mono text-zinc-500 ml-2">({notifications.filter((n: any) => !n.read).length})</span>
          </h2>
          <button onClick={onClose} className="p-3 bg-zinc-900 border border-zinc-800 rounded-full text-zinc-500 hover:text-white hover:border-white hover:bg-black transition-colors">
            <X size={24} />
          </button>
        </div>

        {notifications.length > 0 && (
          <div className="px-6 lg:px-12 py-4 flex flex-col sm:flex-row gap-4 bg-black border-b border-zinc-900">
              <button 
                onClick={handleMarkAllRead} 
                disabled={isProcessing} 
                className="flex-1 lg:flex-none lg:w-48 py-4 bg-zinc-900 border border-zinc-800 lg:rounded-none text-xs font-black text-white uppercase tracking-[0.2em] flex items-center justify-center gap-3 hover:bg-white hover:text-black transition-colors disabled:opacity-50"
              >
                <CheckCircle size={16} /> Mark All Read
              </button>
              <button 
                onClick={handleClearAll} 
                disabled={isProcessing} 
                className="flex-1 lg:flex-none lg:w-48 py-4 bg-zinc-900 border border-zinc-800 lg:rounded-none text-xs font-black text-white uppercase tracking-[0.2em] flex items-center justify-center gap-3 hover:border-red-500 hover:text-red-500 hover:bg-black transition-colors disabled:opacity-50"
              >
                <Trash2 size={16} /> Clear All
              </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 lg:px-12 py-4 space-y-2 lg:space-y-0 bg-black scrollbar-hide">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 text-zinc-600 opacity-50">
              <Bell size={80} strokeWidth={1} className="mb-6" />
              <p className="font-black uppercase tracking-[0.3em] text-xl text-white">Silence</p>
              <p className="font-mono uppercase tracking-widest text-xs mt-2">You're all caught up</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 lg:gap-6 lg:pb-12">
                {notifications.map((n: any) => (
                <div 
                    key={n.id} 
                    onClick={() => handleNotificationClick(n)} 
                    className={`relative group p-6 rounded-2xl lg:rounded-none flex items-start gap-5 cursor-pointer border transition-all hover:bg-zinc-900 ${
                        n.read ? 'bg-black border-transparent opacity-60' : 'bg-zinc-950 border-zinc-800'
                    }`}
                >
                    
                    <div className={`w-14 h-14 rounded-full lg:rounded-none flex items-center justify-center shrink-0 border ${
                    n.type === 'order' ? 'bg-white text-black border-white' :
                    n.type === 'drop_reminder' ? 'bg-green-500/10 text-green-400 border-green-500/30' : // 🟢 ADDED GREEN STYLE
                    n.type === 'reward' ? 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30' :
                    n.type === 'mention' || n.type === 'message' ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30' :
                    n.type === 'sale' ? 'bg-zinc-900 text-white border-zinc-700' :
                    n.type === 'follow_request' ? 'bg-zinc-900 text-white border-zinc-700' :
                    'bg-zinc-900 text-white border-zinc-800'
                    }`}>
                    {n.type === 'order' ? <Package size={24} /> : 
                    n.type === 'drop_reminder' ? <Clock size={24} /> : // 🟢 ADDED CLOCK ICON
                    n.type === 'reward' ? <Trophy size={24} /> : 
                    n.type === 'mention' || n.type === 'message' ? <MessageCircle size={24} /> : 
                    n.type === 'like' ? <Heart size={24} /> : 
                    n.type === 'follow' ? <Info size={24} /> : 
                    n.type === 'follow_request' ? <UserPlus size={24} /> :
                    n.type === 'sale' ? <Tag size={24} /> :
                    <Bell size={24} />}
                    </div>

                    <div className="flex-1 min-w-0 pt-1 pr-6">
                        <div className="flex justify-between items-start mb-2">
                            <h4 className={`text-base truncate pr-2 ${n.read ? 'font-bold text-zinc-400' : 'font-black text-white uppercase tracking-tight'}`}>
                                {n.title}
                            </h4>
                            <span className="text-[10px] text-zinc-500 font-mono whitespace-nowrap uppercase tracking-widest">
                            {n.createdAt?.seconds ? format(new Date(n.createdAt.seconds * 1000), 'MMM d, HH:mm') : 'Now'}
                            </span>
                        </div>
                        <p className="text-sm text-zinc-400 line-clamp-2 leading-relaxed font-mono">{n.message}</p>
                        
                        {n.type === 'follow_request' && n.status === 'pending' && n.senderId && (
                            <div className="flex gap-2 mt-4">
                                <button onClick={(e) => handleAcceptFollow(e, n.id, n.senderId)} className="flex-1 bg-white text-black font-black uppercase text-[10px] py-3 rounded-xl lg:rounded-none tracking-widest hover:bg-zinc-200 transition-colors shadow-xl">Accept</button>
                                <button onClick={(e) => handleDeclineFollow(e, n.id)} className="flex-1 bg-black border border-zinc-800 text-white font-black uppercase text-[10px] py-3 rounded-xl lg:rounded-none tracking-widest hover:border-red-500 hover:text-red-500 transition-colors">Decline</button>
                            </div>
                        )}
                        {n.type === 'follow_request' && n.status === 'accepted' && (
                            <div className="mt-4 text-[10px] text-zinc-500 font-bold uppercase tracking-widest flex items-center gap-2"><CheckCircle size={14} className="text-white" /> Request Accepted</div>
                        )}

                        {/* 🟢 DYNAMIC ACTION TEXTS */}
                        {n.type === 'drop_reminder' && (
                            <p className="text-[10px] font-black text-green-400 mt-4 uppercase tracking-[0.2em] flex items-center gap-2 group-hover:text-green-300 transition-colors">
                                View Drop <ArrowRight size={14}/>
                            </p>
                        )}
                        
                        {n.type === 'sale' && (
                            <p className="text-[10px] font-black text-white mt-4 uppercase tracking-[0.2em] flex items-center gap-2 group-hover:text-zinc-300 transition-colors">
                                Tap to Shop <ArrowRight size={14}/>
                            </p>
                        )}
                        
                        {n.type === 'reward' && (
                            <p className="text-[10px] font-black text-yellow-500 mt-4 uppercase tracking-[0.2em] flex items-center gap-2 group-hover:text-yellow-400 transition-colors">
                                View in Vault <ArrowRight size={14}/>
                            </p>
                        )}

                        {(n.type === 'mention' || n.type === 'message') && (
                            <p className="text-[10px] font-black text-cyan-400 mt-4 uppercase tracking-[0.2em] flex items-center gap-2 group-hover:text-cyan-300 transition-colors">
                                View Message <ArrowRight size={14}/>
                            </p>
                        )}
                    </div>

                    {!n.read && (
                        <div className="absolute top-1/2 -translate-y-1/2 right-6 w-3 h-3 rounded-full bg-white shadow-[0_0_12px_#ffffff]"></div>
                    )}
                </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};